type ValidationSeverity = 'BLOCKING' | 'WARNING'

export type ActivityValidationIssue = {
  severity: ValidationSeverity
  activityId?: number
  date?: string
  message: string
  kind?:
    | 'MISSING_SERVICE_RECIPIENT'
    | 'INVALID_TIME_QUANTITY'
    | 'MISSING_ACTIVITY_TYPE'
    | 'SERVICE_NOT_CONFIGURED'
    | 'MISSING_ORDER_REFERENCE'
    | 'UNKNOWN'
}

const RX_ACTIVITY = /Activity ID (\d+)\s*\(date:\s*([^)]+)\)\s*(.*)$/i

/**
 * German text shown to the user for each classified validation issue. The backend returns
 * these validation errors in English; we classify them into a `kind` and render the German
 * message here so nothing English reaches the invoice-creation dialog (SIB-859). UNKNOWN is
 * the catch-all: even an unclassified backend line becomes a safe German sentence rather
 * than leaking the raw English text.
 */
const GERMAN_ISSUE_REASON: Record<NonNullable<ActivityValidationIssue['kind']>, string> = {
  MISSING_SERVICE_RECIPIENT: 'Kein Leistungsempfänger hinterlegt.',
  INVALID_TIME_QUANTITY: 'Ungültige Stundenanzahl.',
  MISSING_ACTIVITY_TYPE: 'Keine Leistungsart angegeben.',
  SERVICE_NOT_CONFIGURED:
    'Die Leistung ist nicht konfiguriert. Bitte hinterlegen Sie sie in den Einstellungen.',
  MISSING_ORDER_REFERENCE: 'Keine Bestellreferenz vorhanden.',
  UNKNOWN: 'Diese Aktivität kann derzeit nicht abgerechnet werden.',
}

export function germanValidationMessage(
  kind: ActivityValidationIssue['kind'],
  activityId?: number,
  date?: string
): string {
  const reason = GERMAN_ISSUE_REASON[kind ?? 'UNKNOWN']
  if (activityId == null) return reason

  const datePart = date ? ` (Datum: ${date})` : ''
  return `Aktivität ${activityId}${datePart}: ${reason}`
}

function classify(msg: string): {
  severity: ValidationSeverity
  kind: ActivityValidationIssue['kind']
} {
  const m = msg.toLowerCase()

  // warning (currently non-blocking)
  if (m.includes('no order reference') || m.includes('(orderid)')) {
    return { severity: 'WARNING', kind: 'MISSING_ORDER_REFERENCE' }
  }

  // blocking
  if (m.includes('no service recipient') || m.includes('(customerid)')) {
    return { severity: 'BLOCKING', kind: 'MISSING_SERVICE_RECIPIENT' }
  }
  if (m.includes('invalid time quantity')) {
    return { severity: 'BLOCKING', kind: 'INVALID_TIME_QUANTITY' }
  }
  if (m.includes('no activity type')) {
    return { severity: 'BLOCKING', kind: 'MISSING_ACTIVITY_TYPE' }
  }
  if (m.includes('service not found') || m.includes('ensure this service is configured')) {
    return { severity: 'BLOCKING', kind: 'SERVICE_NOT_CONFIGURED' }
  }

  return { severity: 'BLOCKING', kind: 'UNKNOWN' } // safest default
}

export function parseInvoiceValidationError(raw: unknown): {
  title: string
  blocking: ActivityValidationIssue[]
  warnings: ActivityValidationIssue[]
  all: ActivityValidationIssue[]
} | null {
  // Adapt this to your API error shape:
  // common patterns: { code, message }, or { error: { code, message } }, etc.
  const err: any = raw
  const code = err?.code ?? err?.error?.code
  const message: string =
    err?.message ?? err?.error?.message ?? err?.response?.data?.message ?? err?.data?.message ?? ''

  const looksLikeValidation =
    String(code).toUpperCase().includes('VALIDATION') ||
    message.startsWith('Cannot create invoice due to validation errors')

  if (!looksLikeValidation) return null

  const lines = message
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean)

  // drop header line if present
  const detailLines = lines.filter(
    l => !l.toLowerCase().startsWith('cannot create invoice due to validation errors')
  )

  const issues: ActivityValidationIssue[] = detailLines.map(line => {
    const match = line.match(RX_ACTIVITY)
    if (match) {
      const activityId = Number(match[1])
      const date = match[2]
      const rest = match[3] || ''
      // Classify from the raw English line, then display the German equivalent.
      const englishMsg = `Activity ID ${activityId} (date: ${date}) ${rest}`.trim()
      const { severity, kind } = classify(englishMsg)
      return {
        severity,
        kind,
        activityId,
        date,
        message: germanValidationMessage(kind, activityId, date),
      }
    }

    const { severity, kind } = classify(line)
    return { severity, kind, message: germanValidationMessage(kind) }
  })

  const blocking = issues.filter(i => i.severity === 'BLOCKING')
  const warnings = issues.filter(i => i.severity === 'WARNING')

  return {
    title: 'Rechnung konnte nicht erstellt werden',
    blocking,
    warnings,
    all: issues,
  }
}
